import { DialogTitle } from '@headlessui/react'
import {
  CheckIcon,
  RectangleStackIcon,
  UserGroupIcon
} from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Skeleton from 'react-loading-skeleton'

import {
  useNotificationContext,
  usePartnerContext,
  useUserContext
} from '../../context'
import {
  useSampleDataDialog,
  useSampleDataDialogState
} from '../../context/sample-data-context'
import {
  SAMPLE_DATA_AVAILABILITY,
  isSampleTemplatePartner,
  summarizeTitles
} from '../../helpers/sample-data'
import {
  useAddSampleData,
  useRefreshSampleData,
  useRemoveSampleData,
  useSampleDataAccess,
  useSampleDataSummary
} from '../../hooks/use-sample-data'
import Alert from '../alert'
import Button from '../tailwind/Button'
import Modal from '../tailwind/Modal'

const P = 'sample_data.'

const VIEWS = {
  LOADING: 'LOADING',
  SUMMARY_ERROR: 'SUMMARY_ERROR',
  REMOVE: 'REMOVE',
  CAREER_REMOVE: 'CAREER_REMOVE',
  INFO: 'INFO',
  TEMPLATE: 'TEMPLATE',
  NOTHING: 'NOTHING',
  ADD: 'ADD'
}

const isCareerScope = (scope) =>
  scope?.career_ids?.length === 1 && !scope?.space_ids?.length

// Welche Ansicht der Dialog zeigt. Während und nach einem Lauf bleibt sie
// stehen (snapshot), sonst sprängen die Invalidierungen nach dem Entfernen
// noch im offenen Dialog auf "Nichts mehr da".
const getView = ({ mode, scope, access, summary, data, snapshot }) => {
  if (access.isTemplatePartner) return VIEWS.TEMPLATE
  if (mode === 'add') return VIEWS.ADD
  if (snapshot) {
    return isCareerScope(scope) ? VIEWS.CAREER_REMOVE : VIEWS.REMOVE
  }
  if (!summary.isFetchedAfterMount || (summary.isError && summary.isFetching)) {
    return VIEWS.LOADING
  }
  if (summary.isError || !data) return VIEWS.SUMMARY_ERROR
  if (data.blocked) {
    if (data.blocked.reason === 'PROTECTED_PARTNER') return VIEWS.TEMPLATE
    if (data.blocked.reason === 'NO_PERMISSION') return VIEWS.INFO
    return VIEWS.SUMMARY_ERROR
  }
  if (data.isEmpty) return VIEWS.NOTHING
  if (
    mode === 'info' ||
    access.gate !== null ||
    data.permissions.can_remove === false
  ) {
    return VIEWS.INFO
  }

  return isCareerScope(scope) ? VIEWS.CAREER_REMOVE : VIEWS.REMOVE
}

const Title = ({ children }) => (
  <DialogTitle
    as='h2'
    className='pr-10 text-2xl font-bold tracking-tight text-gray-900'
  >
    {children}
  </DialogTitle>
)

Title.propTypes = { children: PropTypes.node.isRequired }

const Text = ({ children }) => (
  <p className='mt-2 text-sm text-gray-500'>{children}</p>
)

Text.propTypes = { children: PropTypes.node.isRequired }

// Box- und Zeilenmuster wie modal-delete-user.js
const Box = ({ title, children }) => (
  <div className='mt-5 rounded-lg border border-gray-200 bg-gray-50'>
    <div className='border-b border-gray-200 px-4 py-2.5'>
      <p className='text-xs font-medium uppercase tracking-wide text-gray-500'>
        {title}
      </p>
    </div>
    <ul className='divide-y divide-gray-100'>{children}</ul>
  </div>
)

Box.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired
}

const Row = ({ icon: Icon, title, lines }) => (
  <li className='flex gap-3 px-4 py-3'>
    <Icon
      className='mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400'
      aria-hidden='true'
    />
    <div className='min-w-0'>
      <p className='text-sm text-gray-900'>{title}</p>
      {lines.map((line, index) => (
        <p key={index} className='truncate text-sm text-gray-500'>
          {line}
        </p>
      ))}
    </div>
  </li>
)

Row.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  lines: PropTypes.arrayOf(PropTypes.string)
}

Row.defaultProps = { lines: [] }

const Actions = ({ children }) => (
  <div className='mt-8 flex flex-col-reverse gap-3 sm:grid sm:grid-flow-row-dense sm:grid-cols-2'>
    {children}
  </div>
)

Actions.propTypes = { children: PropTypes.node.isRequired }

const CloseAction = ({ onClose }) => {
  const { t } = useTranslation()

  return (
    <div className='mt-8 flex justify-end'>
      <Button.SecondaryLG text={t(P + 'remove.close')} onClick={onClose} />
    </div>
  )
}

CloseAction.propTypes = { onClose: PropTypes.func.isRequired }

const SampleDataDialogBody = ({
  mode,
  scope,
  isRemoving,
  isAdding,
  failures,
  onRemove,
  onAdd,
  onClose,
  onOpenSupport
}) => {
  const { t } = useTranslation()
  const { partner } = usePartnerContext()
  const { employees } = useUserContext()
  const access = useSampleDataAccess()
  const refresh = useRefreshSampleData()

  const summary = useSampleDataSummary({
    scope,
    enabled: mode !== 'add' && !access.isTemplatePartner
  })

  // Zählung zum Zeitpunkt der Bestätigung, bleibt bis zum Schließen stehen
  const [snapshot, setSnapshot] = useState(null)
  const data = snapshot || summary.data

  const view = getView({ mode, scope, access, summary, data, snapshot })

  // Self-Heal bei veraltetem Cache. Einmal pro Öffnen, der Body wird beim
  // Schließen ausgehängt. Mit Scope, damit auch die offene Detailseite der
  // Stelle oder des Talents neu lädt.
  const healed = useRef(false)
  useEffect(() => {
    if (view !== VIEWS.NOTHING || healed.current) return
    healed.current = true
    refresh(scope)
  }, [view, refresh, scope])

  const confirmRemove = () => {
    // Beim erneuten Versuch bleibt die erste Zählung stehen, die Fehlermeldung
    // sagt schon, dass ein Teil entfernt sein kann
    setSnapshot((prev) => prev || summary.data)
    onRemove()
  }

  const errorBlock = failures > 0 && !isRemoving && (
    <>
      <Alert
        type='error'
        className='mt-5'
        title={t(P + 'remove.error_title')}
        description={t(P + 'remove.error_text')}
      />
      {failures >= 2 && (
        <p className='mt-3 text-sm text-gray-600'>
          {t(P + 'remove.error_support')}{' '}
          <button
            type='button'
            onClick={onOpenSupport}
            className='font-semibold text-blue-600 hover:text-blue-500'
          >
            {t(P + 'remove.error_support_action')}
          </button>
        </p>
      )}
    </>
  )

  const removeActions = (confirmText) => (
    <>
      <Actions>
        <Button.SecondaryLG
          text={t(P + 'remove.cancel')}
          onClick={onClose}
          disabled={isRemoving}
          fullWidth
        />
        <Button.WarningLG
          text={failures > 0 ? t(P + 'remove.retry') : confirmText}
          onClick={confirmRemove}
          isLoading={isRemoving}
          fullWidth
        />
      </Actions>
      {isRemoving && (
        <p className='mt-3 text-center text-sm text-gray-500' role='status'>
          {t(P + 'remove.running')}
        </p>
      )}
    </>
  )

  const careerLines = (careers) => {
    const { visible, more } = summarizeTitles(careers)

    return [
      ...visible.map(({ title, archived }) =>
        archived ? `${title} ${t(P + 'remove.archived_suffix')}` : title
      ),
      ...(more ? [t(P + 'remove.more_titles', { count: more })] : [])
    ]
  }

  const talentLines = ({ released }) => [
    t(P + 'remove.talents_detail'),
    ...(released ? [t(P + 'remove.talents_released', { count: released })] : [])
  ]

  if (view === VIEWS.TEMPLATE) {
    return (
      <>
        <Title>{t(P + 'info.title')}</Title>
        <Text>
          {t(
            P +
              (isSampleTemplatePartner(access.partnerId)
                ? 'info.template'
                : 'info.protected')
          )}
        </Text>
        <CloseAction onClose={onClose} />
      </>
    )
  }

  if (view === VIEWS.ADD) {
    return (
      <>
        <Title>{t(P + 'add.title')}</Title>
        <Text>{t(P + 'add.text')}</Text>
        <Text>{t(P + 'add.text_2')}</Text>
        <Actions>
          <Button.SecondaryLG
            text={t(P + 'add.cancel')}
            onClick={onClose}
            disabled={isAdding}
            fullWidth
          />
          <Button.PrimaryLG
            text={t(P + 'add.confirm')}
            onClick={onAdd}
            isLoading={isAdding}
            fullWidth
          />
        </Actions>
      </>
    )
  }

  if (view === VIEWS.LOADING) {
    return (
      <div aria-busy='true'>
        <Title>
          {t(
            P + (isCareerScope(scope) ? 'career_remove.title' : 'remove.title')
          )}
        </Title>
        <Skeleton count={2} className='mt-2' />
        <Skeleton height={112} className='mt-5' borderRadius='0.5rem' />
        <Actions>
          <Button.SecondaryLG
            text={t(P + 'remove.cancel')}
            onClick={onClose}
            fullWidth
          />
          <Button.WarningLG text={t(P + 'remove.confirm')} disabled fullWidth />
        </Actions>
      </div>
    )
  }

  if (view === VIEWS.SUMMARY_ERROR) {
    return (
      <>
        <Title>{t(P + 'info.title')}</Title>
        <Text>{t(P + 'remove.summary_error')}</Text>
        <Actions>
          <Button.SecondaryLG
            text={t(P + 'remove.close')}
            onClick={onClose}
            fullWidth
          />
          <Button.PrimaryLG
            text={t(P + 'remove.retry')}
            onClick={() => summary.refetch()}
            fullWidth
          />
        </Actions>
      </>
    )
  }

  if (view === VIEWS.NOTHING) {
    return (
      <>
        <Title>{t(P + 'remove.nothing_title')}</Title>
        <Text>{t(P + 'remove.nothing_text')}</Text>
        <CloseAction onClose={onClose} />
      </>
    )
  }

  if (view === VIEWS.INFO) {
    const noPermission =
      access.gate === SAMPLE_DATA_AVAILABILITY.NO_PERMISSION ||
      data.permissions.can_remove === false ||
      data.blocked?.reason === 'NO_PERMISSION'
    const note = noPermission
      ? 'info.no_permission'
      : access.gate === SAMPLE_DATA_AVAILABILITY.ONBOARDING
        ? 'info.onboarding'
        : null

    return (
      <>
        <Title>{t(P + 'info.title')}</Title>
        <Text>{t(P + 'settings.description')}</Text>
        {!data.blocked && (
          <Box title={t(P + 'info.counts_title')}>
            {data.careers.length > 0 && (
              <Row
                icon={RectangleStackIcon}
                title={t(P + 'remove.careers', { count: data.careers.length })}
                lines={careerLines(data.careers)}
              />
            )}
            {data.talents.total > 0 && (
              <Row
                icon={UserGroupIcon}
                title={t(P + 'remove.talents', { count: data.talents.total })}
              />
            )}
          </Box>
        )}
        {note && <p className='mt-5 text-sm text-gray-700'>{t(P + note)}</p>}
        <CloseAction onClose={onClose} />
      </>
    )
  }

  if (view === VIEWS.CAREER_REMOVE) {
    const career = data.careers[0]
    const total = data.talents.total

    return (
      <>
        <Title>{t(P + 'career_remove.title')}</Title>
        <Text>
          {career?.action === 'KEEP'
            ? t(P + 'career_remove.kept', {
                title: career.title,
                count: career.real_talents
              })
            : total > 0
              ? t(P + 'career_remove.text', {
                  title: career?.title,
                  count: total
                })
              : t(P + 'career_remove.text_none', { title: career?.title })}
        </Text>
        {errorBlock}
        {removeActions(t(P + 'career_remove.confirm'))}
      </>
    )
  }

  // VIEWS.REMOVE
  const { visible: keptVisible, more: keptMore } = summarizeTitles(
    data.keptCareers
  )
  const location =
    employees?.length > 1 ? partner.display_name || partner.name : null

  return (
    <>
      <Title>{t(P + 'remove.title')}</Title>
      <Text>
        {location
          ? t(P + 'remove.intro_location', { location })
          : t(P + 'remove.intro')}
      </Text>
      {(data.removableCareers.length > 0 || data.talents.total > 0) && (
        <Box title={t(P + 'remove.removed_title')}>
          {data.removableCareers.length > 0 && (
            <Row
              icon={RectangleStackIcon}
              title={t(P + 'remove.careers', {
                count: data.removableCareers.length
              })}
              lines={careerLines(data.removableCareers)}
            />
          )}
          {data.talents.total > 0 && (
            <Row
              icon={UserGroupIcon}
              title={t(P + 'remove.talents', { count: data.talents.total })}
              lines={talentLines(data.talents)}
            />
          )}
        </Box>
      )}
      {data.partial && (
        <p className='mt-3 text-sm text-gray-500'>
          {t(P + 'remove.partial_hint')}
        </p>
      )}
      <Box title={t(P + 'remove.kept_title')}>
        <Row icon={CheckIcon} title={t(P + 'remove.kept_own')} />
        {keptVisible.map((career) => (
          <Row
            key={career.id}
            icon={CheckIcon}
            title={t(P + 'remove.kept_career', {
              title: career.title,
              count: career.real_talents
            })}
          />
        ))}
        {keptMore > 0 && (
          <Row
            icon={CheckIcon}
            title={t(P + 'remove.kept_more', { count: keptMore })}
          />
        )}
      </Box>
      <p className='mt-5 text-sm text-gray-500'>
        {t(P + 'remove.irreversible')}
      </p>
      {errorBlock}
      {removeActions(t(P + 'remove.confirm'))}
    </>
  )
}

SampleDataDialogBody.propTypes = {
  mode: PropTypes.oneOf(['remove', 'add', 'info']).isRequired,
  scope: PropTypes.shape({
    career_ids: PropTypes.arrayOf(PropTypes.string),
    space_ids: PropTypes.arrayOf(PropTypes.string)
  }),
  isRemoving: PropTypes.bool.isRequired,
  isAdding: PropTypes.bool.isRequired,
  failures: PropTypes.number.isRequired,
  onRemove: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onOpenSupport: PropTypes.func.isRequired
}

SampleDataDialogBody.defaultProps = { scope: undefined }

/**
 * Ein Dialog für Entfernen (global oder im Scope einer Stelle), Info
 * (ohne Löschrecht oder im Onboarding), Vorlagen-Partner, "Nichts mehr da"
 * und Hinzufügen. Geöffnet über useSampleDataDialog().openSampleDataDialog.
 * Während ein Lauf läuft, ist der Dialog gesperrt (kein X, kein Overlay,
 * kein Esc, Abbrechen deaktiviert).
 */
const SampleDataDialog = ({ onOpenSupport }) => {
  const { t } = useTranslation()
  const { success } = useNotificationContext()
  const { open, requestId, mode, scope, source, onSuccess } =
    useSampleDataDialogState()
  const { closeSampleDataDialog } = useSampleDataDialog()

  const {
    mutateAsync: removeSampleData,
    reset: resetRemove,
    isPending: isRemoving
  } = useRemoveSampleData()
  const {
    mutateAsync: addSampleData,
    reset: resetAdd,
    isPending: isAdding
  } = useAddSampleData({ source })

  const [failures, setFailures] = useState(0)

  // Jedes Öffnen startet ohne Fehler aus dem letzten Lauf
  useEffect(() => {
    resetRemove()
    resetAdd()
    setFailures(0)
  }, [requestId, resetRemove, resetAdd])

  const isRunning = isRemoving || isAdding

  const close = () => {
    if (!isRunning) closeSampleDataDialog()
  }

  const handleRemove = async () => {
    try {
      const result = await removeSampleData({ scope, source })
      const kept = result.kept_careers.length

      closeSampleDataDialog()
      success(
        kept > 0
          ? t(P + 'remove.success_kept', { count: kept })
          : t(P + 'remove.success')
      )
      onSuccess?.(result)
    } catch {
      // Fehler steht im Dialog, ab dem zweiten mit Support-Hub-Link
      setFailures((count) => count + 1)
    }
  }

  const handleAdd = async () => {
    try {
      const result = await addSampleData({ source })

      closeSampleDataDialog()
      onSuccess?.(result)
    } catch {
      // Meldung kommt aus useAddSampleData, der Dialog bleibt für einen neuen
      // Versuch offen
    }
  }

  const openSupport = () => {
    closeSampleDataDialog()
    onOpenSupport()
  }

  return (
    <Modal
      open={open}
      setOpen={close}
      size='lg'
      disableOverlayClose={isRunning}
      hideCloseButton={isRunning}
    >
      <SampleDataDialogBody
        key={requestId}
        mode={mode}
        scope={scope}
        isRemoving={isRemoving}
        isAdding={isAdding}
        failures={failures}
        onRemove={handleRemove}
        onAdd={handleAdd}
        onClose={close}
        onOpenSupport={openSupport}
      />
    </Modal>
  )
}

SampleDataDialog.propTypes = {
  onOpenSupport: PropTypes.func.isRequired
}

export default SampleDataDialog
